import { CalendarOff, Check, LayoutGrid } from 'lucide-react';
import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';

import { cn } from '../../shared/lib/cn';

import type { AttendanceSummaryRow, BatchRow } from './api';
import s from './BatchRail.module.css';

export type BatchProgress =
  | { kind: 'off'; label: string }
  | { kind: 'empty'; label: string }
  | { kind: 'none'; label: string }
  | { kind: 'partial'; label: string }
  | { kind: 'done'; label: string };

/**
 * What the chip should say under the batch name. Derived from the summary RPC
 * when it is available; falls back to a plain roster size when it isn't (the
 * migration may not be applied yet), so the rail never renders a hole.
 */
export function batchProgress(batch: BatchRow, summary: AttendanceSummaryRow | undefined) {
  if (summary === undefined) {
    const n = batch.student_count;
    return { kind: 'none', label: `${n} student${n === 1 ? '' : 's'}` } satisfies BatchProgress;
  }
  if (!summary.is_workday) {
    return { kind: 'off', label: 'No class' } satisfies BatchProgress;
  }
  if (summary.student_count === 0) {
    return { kind: 'empty', label: 'No students' } satisfies BatchProgress;
  }
  if (summary.marked_count >= summary.student_count) {
    return { kind: 'done', label: 'All marked' } satisfies BatchProgress;
  }
  if (summary.marked_count === 0) {
    return { kind: 'none', label: `0/${summary.student_count} marked` } satisfies BatchProgress;
  }
  return {
    kind: 'partial',
    label: `${summary.marked_count}/${summary.student_count} marked`,
  } satisfies BatchProgress;
}

const PROGRESS_CLASS: Record<BatchProgress['kind'], string> = {
  off: s.metaOff ?? '',
  empty: s.metaMuted ?? '',
  none: s.metaMuted ?? '',
  partial: s.metaPartial ?? '',
  done: s.metaDone ?? '',
};

/** Aggregate chip label for the "All batches" tab. */
function allProgress(
  batches: BatchRow[],
  summaries: AttendanceSummaryRow[] | null | undefined,
): BatchProgress {
  if (summaries === undefined || summaries === null) {
    const n = batches.reduce((sum, b) => sum + b.student_count, 0);
    return { kind: 'none', label: `${n} student${n === 1 ? '' : 's'}` };
  }
  // Batches that are off today have nothing to mark, so counting them would
  // make "all marked" unreachable.
  const open = summaries.filter((r) => r.is_workday);
  const total = open.reduce((sum, r) => sum + r.student_count, 0);
  const marked = open.reduce((sum, r) => sum + r.marked_count, 0);
  if (total === 0) return { kind: 'off', label: 'No class' };
  if (marked >= total) return { kind: 'done', label: 'All marked' };
  return { kind: marked === 0 ? 'none' : 'partial', label: `${marked}/${total} marked` };
}

interface Props {
  batches: BatchRow[];
  /** `null` when the summary RPC isn't deployed — chips fall back to roster size. */
  summaries: AttendanceSummaryRow[] | null | undefined;
  selectedBatchId: string;
  onSelect: (batchId: string) => void;
  /** Warm the caches for a batch the pointer/focus is merely hovering. */
  onPrefetch: (batchId: string) => void;
  /** Id of the roster region these tabs control. */
  panelId: string;
  /**
   * Sentinel id for an "All batches" tab prepended to the rail. Omit to keep the
   * rail batch-only — with a single batch there is nothing to aggregate.
   */
  allId?: string;
}

export function BatchRail({
  batches,
  summaries,
  selectedBatchId,
  onSelect,
  onPrefetch,
  panelId,
  allId,
}: Props): JSX.Element {
  const listRef = useRef<HTMLDivElement | null>(null);

  const tabs = useMemo(() => {
    const rows = batches.map((batch) => ({
      id: batch.id,
      name: batch.name,
      isAll: false,
      progress: batchProgress(
        batch,
        summaries?.find((r) => r.batch_id === batch.id),
      ),
    }));
    if (allId === undefined) return rows;
    return [
      { id: allId, name: 'All batches', isAll: true, progress: allProgress(batches, summaries) },
      ...rows,
    ];
  }, [batches, summaries, allId]);

  const selectedIndex = Math.max(
    0,
    tabs.findIndex((t) => t.id === selectedBatchId),
  );
  // Manual activation: arrow keys move focus only, Enter/Space commits. Moving
  // the selection on every arrow press would fire a roster query per keystroke.
  const [focusIndex, setFocusIndex] = useState(selectedIndex);

  useEffect(() => {
    setFocusIndex(selectedIndex);
  }, [selectedIndex]);

  // Keep the active chip on screen when the rail overflows — on a phone the
  // selected batch is often scrolled out of view after a reload.
  useEffect(() => {
    const tabs = listRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
    // eslint-disable-next-line security/detect-object-injection -- selectedIndex is a bounded array index derived from findIndex
    const tab = tabs?.[selectedIndex];
    // Purely cosmetic, and not implemented in every environment (jsdom has no
    // layout engine) — never let it take the rail down.
    if (typeof tab?.scrollIntoView === 'function') {
      tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [selectedIndex]);

  function focusTab(index: number): void {
    const tabs = listRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
    if (tabs === undefined || tabs.length === 0) return;
    const bounded = (index + tabs.length) % tabs.length;
    setFocusIndex(bounded);
    // eslint-disable-next-line security/detect-object-injection -- `bounded` is wrapped into 0..length-1 on the line above
    tabs[bounded]?.focus();
  }

  function handleKey(e: KeyboardEvent<HTMLDivElement>): void {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      focusTab(focusIndex + 1);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      focusTab(focusIndex - 1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      focusTab(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      focusTab(tabs.length - 1);
    }
  }

  return (
    <div
      ref={listRef}
      className={s.rail}
      role="tablist"
      aria-label="Batch"
      aria-orientation="horizontal"
      // Container is not a tab stop itself — focus always lands on a tab. The
      // handler lives here so arrow keys work from whichever tab has focus.
      tabIndex={-1}
      onKeyDown={handleKey}
    >
      {tabs.map((tab, i) => {
        const selected = tab.id === selectedBatchId;
        const { progress } = tab;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`batch-tab-${tab.id}`}
            aria-selected={selected}
            aria-controls={panelId}
            tabIndex={i === focusIndex ? 0 : -1}
            className={cn(s.chip, selected ? s.chipActive : null, tab.isAll ? s.chipAll : null)}
            onClick={() => onSelect(tab.id)}
            onMouseEnter={() => (tab.isAll ? undefined : onPrefetch(tab.id))}
            onFocus={() => (tab.isAll ? undefined : onPrefetch(tab.id))}
          >
            <span className={s.chipName}>
              {tab.isAll ? <LayoutGrid size={13} aria-hidden="true" /> : null}
              {tab.name}
            </span>
            <span className={cn(s.chipMeta, PROGRESS_CLASS[progress.kind])}>
              {progress.kind === 'done' ? (
                <Check size={12} aria-hidden="true" />
              ) : progress.kind === 'off' ? (
                <CalendarOff size={12} aria-hidden="true" />
              ) : null}
              {progress.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
